import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, of, tap, map, switchMap, catchError } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { Bid, BidMeta, BidStatus, BidWonDetails, CreateProjectsFromBidResult, CreatedProjectSummary, TrackerCloneType, VendorSendResult } from '../../_models/bid';
import { BidFieldPlan } from '../../_models/field-plan';
import { BidSpecParseResult } from './bid-spec-parsing';
import { environment } from 'projects/core/src/environments/environment';

const ZOHO_BASE = environment.apiEndpoint + '/zoho/';

/** One financial line for the Zoho deal's Items subform — a service, or a sample segment. */
export interface ZohoDealItemPayload {
  /** Zoho product to link, by name — 'Sample' for sample lines, the service name for service lines. */
  name: string;
  description: string;
  /** Sample lines only: completes (N); Zoho computes qty × rate as the line amount. */
  qty?: number;
  /** Incidence rate as a whole percent (20 = 20%). */
  ir?: number;
  /** Interview length in minutes. */
  loi?: number;
  /** Client CPI — Zoho's "Customer Rate". */
  rate?: number;
  /** Service lines only: flat client price (no qty/rate). */
  amount?: number;
}

/** The option selection + financials posted with deal creation (`POST zoho/deal/{bidId}`). */
export interface ZohoDealPayload {
  /** Vendor-bids scenario keys of the chosen option(s) — persisted on the bid as `sentOptionKeys`. */
  selectedKeys: string[];
  /** Deal total client price: services + the selected options' sample. */
  amount: number;
  items: ZohoDealItemPayload[];
}

// The Closed Won snapshot types (BidWonDetails and friends) live in `_models/bid.ts` — they are
// part of the Bid shape now that the API returns `wonDetails` on the bid itself.

/** A bid match for the bid-workspace nav-bar search — shaped for the shared `or-global-search`. */
export interface BidSearchResult {
  id: string;
  /** The bid number, for an exact-match lookup (the `/bids/find/:number` route). */
  number: string;
  /** Primary line — the bid name. */
  label: string;
  /** Secondary line — e.g. "Bid #123". */
  subLabel: string;
  /** The bid's own tracker ID (CCC-YY-N00 with its wave, e.g. ORH-26-102) when it belongs to a tracker. */
  trackerId?: string | null;
  /** Discriminator the shared search + nav-bar routing key off. Always 'bid'. */
  type: 'bid';
}

export interface MarkSentResult {
  /** The persisted bid — Sent to Client, with `zohoDealId` set when the deal was created. */
  bid: Bid;
  /** Set when the status saved but the Zoho deal creation failed — the bid is still marked sent. */
  dealError?: string;
}

@Injectable({ providedIn: 'root' })
export class BidService {
  baseUrl = environment.apiEndpoint + '/bid/';

  private bids$ = new BehaviorSubject<Bid[]>([]);

  constructor(private http: HttpClient) {}

  /** No statuses = full list (dashboard); with statuses = only bids matching any of them (list chips). */
  getBids(statuses?: BidStatus[]): Observable<Bid[]> {
    const params = statuses?.length ? statuses.map(s => `statuses=${s}`) : [];
    const query = params.length ? '?' + params.join('&') : '';
    return this.http.get<Bid[]>(this.baseUrl + query).pipe(
      tap(bids => this.bids$.next(bids))
    );
  }

  getBidById(id: string): Observable<Bid> {
    return this.http.get<Bid>(this.baseUrl + id);
  }

  /**
   * Bid-workspace search: server-side match over bid *content* (sample-plan location — DMA / county /
   * states / ZIPs / regions / geography — plus audience / GTA / demographics), free-text notes, and the
   * bid's own free text (name, number, additional/internal notes, spec text). Returns bid matches for the
   * shared `or-global-search` in the bid-workspace nav bar. Includes **aged-out** bids (unlike the bids
   * list). Kept separate from the project global search so bids never surface there.
   *
   * NOTE: the backend endpoint is not built yet — builder-ready contract in
   * `.claude/features/NK-222/search-bid-content-and-notes.md`. Until it ships this errors, which the
   * typeahead treats as "no results".
   */
  searchBids(q: string): Observable<BidSearchResult[]> {
    return this.http.get<BidSearchResult[]>(this.baseUrl + 'search', { params: { q } });
  }

  /** Email the given vendors' Bidding & Feasibility contacts a link to the bid; returns per-vendor results. */
  sendVendors(bidId: string, vendorIds: string[]): Observable<VendorSendResult[]> {
    return this.http.post<VendorSendResult[]>(this.baseUrl + bidId + '/sendvendors', vendorIds);
  }

  /** Cheap freshness probe — just the bid's last-modified timestamp, no heavy blobs. */
  getBidMeta(id: string): Observable<BidMeta> {
    return this.http.get<BidMeta>(`${this.baseUrl}${id}/meta`);
  }

  /** The Navigator project(s) created from this bid — the Sold view's links to the converted projects. */
  getBidProjects(id: string): Observable<CreatedProjectSummary[]> {
    return this.http.get<CreatedProjectSummary[]>(`${this.baseUrl}${id}/projects`);
  }

  getBid(id: string): Bid | undefined {
    return this.bids$.value.find(b => b.id === id);
  }

  addBid(bid: Bid): void {
    this.bids$.next([bid, ...this.bids$.value]);
  }

  updateBid(bid: Bid): void {
    this.bids$.next(this.bids$.value.map(b => (b.id === bid.id ? { ...bid } : b)));
  }

  /** Removes a bid from the in-memory list without a server call (used after the DELETE resolves). */
  removeBidFromCache(id: string): void {
    this.bids$.next(this.bids$.value.filter(b => b.id !== id));
  }

  deleteBid(id: string): Observable<void> {
    return this.http.delete<void>(this.baseUrl + id).pipe(
      tap(() => this.removeBidFromCache(id))
    );
  }

  saveBid(bid: Bid): Observable<Bid> {
    return this.http.post<Bid>(this.baseUrl, { ...bid });
  }

  updateBidStatus(id: string, status: BidStatus): Observable<Bid> {
    return this.http.patch<Bid>(`${this.baseUrl}${id}/status`, status).pipe(
      tap(updated => this.updateBid(updated))
    );
  }

  /** How a won bid's NAV project ran, for the clone prompt's imports. `wave` / `renewal` read the
   *  tracker's most recent wave; `copy` reads the bid's own project. Null when there's no NAV project. */
  getFieldPlan(id: string, forClone: 'wave' | 'renewal' | 'copy'): Observable<BidFieldPlan | null> {
    return this.http.get<BidFieldPlan | null>(`${this.baseUrl}${id}/field-plan`, { params: { for: forClone } });
  }

  /** Links a tracker bid that wasn't cloned to an existing tracker as its next wave or a renewal, or
   *  unlinks it (null tracker). The API refuses a tracker that already has a bid. */
  linkTracker(id: string, trackerRecordId: string | null, trackerCloneType: TrackerCloneType | null): Observable<Bid> {
    return this.http.put<Bid>(`${this.baseUrl}${id}/tracker-link`, { trackerRecordId, trackerCloneType }).pipe(
      tap(updated => this.updateBid(updated))
    );
  }

  /** Mark a bid Closed Lost and record the reason chosen in the Closed Lost modal. */
  markBidLost(id: string, reason: string): Observable<Bid> {
    return this.http.patch<Bid>(`${this.baseUrl}${id}/lost`, { reason }).pipe(
      tap(updated => this.updateBid(updated))
    );
  }

  getBidSpecParsing(spec: string): Observable<BidSpecParseResult> {
    return this.http.post<BidSpecParseResult>(this.baseUrl + 'bid-spec-parsing', { spec });
  }

  submitVendorResponse(id: string, scenarios: { key: string; feas: string; cpi: string; dif: string }[]): Observable<void> {
    return this.http.patch<void>(`${this.baseUrl}${id}/vendor-response`, { scenarios });
  }

  /**
   * Convert a bid into its project(s). `selectedKeys` are the winning scenario keys (`${code}:${specIdx}`)
   * chosen in the Closed Won modal — one per country for a Global bid; omit to convert every planned scenario.
   * `wonDetails` is the full Closed Won snapshot: the server persists it to `Bid.WonDetails`, builds each
   * project's name/dates/field-dates from its `projects` entries, and the subsequent Zoho won rungs read
   * it server-side; omit for the auto-create-on-won path (bid-level name/dates, no field dates, no Zoho).
   * The bid is marked Closed Won as part of this call.
   */
  createProjectsFromBid(
    id: string,
    selectedKeys?: string[],
    wonDetails?: BidWonDetails,
  ): Observable<CreateProjectsFromBidResult> {
    return this.http.post<CreateProjectsFromBidResult>(`${this.baseUrl}${id}/create-projects`, { selectedKeys, wonDetails });
  }

  /** Create the bid's Zoho deal, or re-sync an existing one — the endpoint upserts by `zohoDealId`. */
  sendToZoho(bidId: string, payload: ZohoDealPayload): Observable<{ zohoDealId: string }> {
    return this.http.post<{ zohoDealId: string }>(`${ZOHO_BASE}deal/${bidId}`, payload);
  }

  /**
   * Mark a bid Sent to Client, then create or update its Zoho deal with the chosen option's
   * financials. A failed status save errors the stream; a failed deal call resolves with
   * `dealError` set, since the status flip has already persisted.
   */
  markSentAndCreateDeal(bid: Bid, payload: ZohoDealPayload): Observable<MarkSentResult> {
    return this.saveBid({ ...bid, status: BidStatus.SentToClient }).pipe(
      switchMap(saved => {
        if (!saved.id) return of({ bid: saved });
        return this.sendToZoho(saved.id, payload).pipe(
          map(res => ({ bid: { ...saved, zohoDealId: res.zohoDealId, sentOptionKeys: payload.selectedKeys } })),
          catchError(err => of({
            bid: saved,
            dealError: err?.error?.message ?? 'Bid marked as sent, but creating the Zoho deal failed. Please try again.',
          })),
        );
      }),
    );
  }

  deleteZohoDeal(bidId: string): Observable<void> {
    return this.http.delete<void>(`${ZOHO_BASE}deal/${bidId}`);
  }

  /**
   * Push the persisted Closed Won details onto the bid's Zoho deal (creates the deal when the bid
   * was never sent to the client). Parameterless — the server reads `Bid.WonDetails`.
   */
  updateDealFromWon(bidId: string): Observable<{ zohoDealId: string }> {
    return this.http.post<{ zohoDealId: string }>(`${ZOHO_BASE}deal/${bidId}/won`, {});
  }

  /**
   * Flip the deal to the won stage — the money event: Zoho's internal workflow creates the Sales
   * Order on this transition. Idempotent server-side (`changed: false` when already won).
   */
  markDealStageWon(bidId: string): Observable<{ stage: string; changed: boolean }> {
    return this.http.post<{ stage: string; changed: boolean }>(`${ZOHO_BASE}deal/${bidId}/won/stage`, {});
  }

  /**
   * Kick off the Sales Order field sync: Zoho's own workflow creates the SO minutes after the stage
   * flip, so the server pushes the won dates + vendor cost onto it as soon as it appears (found=true
   * when it already existed; polling=true while a background watcher waits for it). Idempotent —
   * also the recovery path if the watcher times out (~10 min).
   */
  syncSalesOrderWon(bidId: string): Observable<{ found: boolean; polling: boolean; salesOrderId: string | null }> {
    return this.http.post<{ found: boolean; polling: boolean; salesOrderId: string | null }>(
      `${ZOHO_BASE}deal/${bidId}/won/salesorder`, {});
  }

  /**
   * The Zoho rungs of Closed Won, in order: deal update from the persisted won details, the stage
   * flip that fires the Sales Order, then the SO field-sync kickoff (which returns immediately and
   * finishes in the background once Zoho creates the SO). Parameterless and idempotent, so safe to
   * re-run after a partial failure (the converted bid itself can no longer be re-saved).
   */
  syncDealWon(bidId: string): Observable<{ found: boolean; polling: boolean; salesOrderId: string | null }> {
    return this.updateDealFromWon(bidId).pipe(
      switchMap(() => this.markDealStageWon(bidId)),
      switchMap(() => this.syncSalesOrderWon(bidId)),
    );
  }
}
